import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

import SpinnerButton from '@/components/common/SpinnerButton';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter
} from '@/components/ui/card';
import { Form } from '@/components/ui/form';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import HorizontalLogo from '@/components/common/HorizontalLogo';
import { useToken } from '@/context/useToken';
import Page from '@/components/layout/Page';
import Checkbox from '@/components/form/Checkbox';

import { usePostUser } from '@/data/user';
import Input from '@/components/form/Input';

const formSchema = z
  .object({
    firstname: z.string().min(1, { message: 'First name is required' }),
    lastname: z.string().min(1, { message: 'Last name is required' }),
    email: z.string().email({ message: 'Invalid email address' }),
    password: z
      .string()
      .min(6, { message: 'Password must be at least 6 characters' }),
    confirmPassword: z.string(),
    isAgreedToTerms: z.boolean().refine((val) => val === true, {
      message: 'You must agree to the terms and privacy policy'
    })
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword']
  });

const SignupPage = () => {
  const { setToken } = useToken();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const postUser = usePostUser();
  const navigate = useNavigate();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstname: '',
      lastname: '',
      email: '',
      password: '',
      confirmPassword: ''
    }
  });

  const onSubmit = async (values, isCoach = false) => {
    setSubmitting(true);
    try {
      const payload = {
        email: values.email,
        firstname: values.firstname,
        lastname: values.lastname,
        password: values.password
      };
      const token = await postUser(payload);
      setError('');
      setSubmitting(false);
      setToken(token);
      navigate(isCoach ? '/application/add' : '/');
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <Page>
      <div className="p-8">
        <HorizontalLogo />
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Welcome!</CardTitle>
          <CardDescription>Signup</CardDescription>
        </CardHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((values) => onSubmit(values, false))}
          >
            <CardContent>
              <div className="flex gap-4">
                <div className="w-1/2">
                  <Input
                    name="firstname"
                    label="First Name"
                    placeholder="First Name..."
                  />
                </div>
                <div className="w-1/2">
                  <Input
                    name="lastname"
                    label="Last Name"
                    placeholder="Last Name..."
                  />
                </div>
              </div>
              <Input name="email" label="Email" placeholder="enter email..." />
              <Input
                name="password"
                label="Password"
                type="password"
                placeholder="enter password..."
              />
              <Input
                name="confirmPassword"
                label="Confirm Password"
                type="password"
                placeholder="confirm password..."
              />
              <div className="mb-4">
                <Checkbox
                  name="isAgreedToTerms"
                  label={
                    <>
                      I have read and agree to the{' '}
                      <a
                        href="https://profound-img.s3.us-west-2.amazonaws.com/contract-resources/profound-terms-and-conditions.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        Terms and Conditions
                      </a>{' '}
                      and{' '}
                      <a
                        href="https://profound-img.s3.us-west-2.amazonaws.com/contract-resources/profound-privacy-policy-may-1-2025.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        Privacy Policy
                      </a>
                    </>
                  }
                />
              </div>
              {error && (
                <Alert variant="destructive">
                  <AlertCircle size={18} />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </CardContent>
            <CardFooter className="flex justify-end gap-4">
              <SpinnerButton
                type="button"
                variant="outline"
                loading={submitting}
                onClick={form.handleSubmit((values) => onSubmit(values, true))}
              >
                Signup as Coach
              </SpinnerButton>
              <SpinnerButton type="submit" loading={submitting}>
                Signup
              </SpinnerButton>
            </CardFooter>
          </form>
        </Form>
      </Card>
      <div className="flex justify-center py-8"></div>
    </Page>
  );
};

export default SignupPage;
